import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Code2, Pencil, Trash2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/orgApi";
import { canWriteMainTab } from "@/lib/maintabAccess";
import { LOC } from "@/constants/testIds";
import LocationForm from "@/components/location/LocationForm";
import FhirViewer from "@/components/organization/FhirViewer";
import "@/styles/emr-catalog.css";

const typeOf = (r) => r.type?.[0]?.coding?.[0]?.display || "—";
const orgOf = (r) => r.managingOrganization?.display || "—";
const cityState = (r) => {
  const a = r.address;
  if (!a) return "—";
  return [a.city, a.state].filter(Boolean).join(", ") || "—";
};

const STATUS_CLASS = {
  active: "emr-catalog__status--active",
  suspended: "emr-catalog__status--suspended",
  inactive: "emr-catalog__status--inactive",
};

export default function LocationsPage() {
  const { user } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "locations");
  const [locs, setLocs] = useState([]);
  const [demoIds, setDemoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fhirTarget, setFhirTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (name) => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations", { params: name ? { name } : {} });
      setLocs(data.items || []);
      setDemoIds(new Set(data.demoIds || []));
    } catch {
      toast.error("Failed to load locations.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/locations/${deleteTarget.id}`);
      toast.success("Location deleted.");
      setDeleteTarget(null);
      load(search.trim() || undefined);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  return (
    <div data-testid={LOC.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={LOC.title} className="emr-catalog__title">
            Locations
          </h1>
          <p className="emr-catalog__subtitle">
            Physical places of care — FHIR <span className="font-fhir">Location</span> resource
          </p>
        </div>
        {canWrite ? (
          <Button
            data-testid={LOC.newBtn}
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="btn emr-catalog__btn"
          >
            <Plus className="emr-catalog__btn-icon" /> New Location
          </Button>
        ) : null}
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Maintain physical and virtual places of care used by appointments, encounters, and users.
            </p>
          </section>
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Search</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-only">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={LOC.search}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name…"
                  className="emr-catalog__input"
                />
              </div>
            </div>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Locations</h2>
          {loading ? (
            <div data-testid={LOC.loading} className="emr-catalog__status-msg">Loading…</div>
          ) : locs.length === 0 ? (
            <div data-testid={LOC.emptyState} className="emr-catalog__empty">
              <MapPin className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No locations yet. Add a practice location.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={LOC.table} className="striped highlight emr-catalog__table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Organization</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {locs.map((r) => (
                    <tr key={r.id} data-testid={`${LOC.row}-${r.id}`}>
                      <td className="emr-catalog__name">
                        {r.name}
                        {demoIds.has(r.id) && (
                          <span data-testid={`demo-badge-${r.id}`} className="emr-catalog__demo">DEMO</span>
                        )}
                      </td>
                      <td className="emr-catalog__muted">{typeOf(r)}</td>
                      <td className="emr-catalog__muted">{orgOf(r)}</td>
                      <td className="emr-catalog__muted">{cityState(r)}</td>
                      <td>
                        <span className={`emr-catalog__status ${STATUS_CLASS[r.status] || STATUS_CLASS.inactive}`}>
                          {r.status || "unknown"}
                        </span>
                      </td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          <Button variant="ghost" size="icon" title="View FHIR" className="emr-catalog-icon-btn"
                            data-testid={`${LOC.viewFhirBtn}-${r.id}`} onClick={() => setFhirTarget(r)}>
                            <Code2 className="h-4 w-4" />
                          </Button>
                          {canWrite ? (
                            <Button variant="ghost" size="icon" title="Edit" className="emr-catalog-icon-btn"
                              data-testid={`${LOC.editBtn}-${r.id}`}
                              onClick={() => { setEditing(r); setFormOpen(true); }}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : null}
                          {canWrite ? (
                            <Button variant="ghost" size="icon" title="Delete" className="emr-catalog-icon-btn emr-catalog-icon-btn--danger"
                              data-testid={`${LOC.deleteBtn}-${r.id}`} onClick={() => setDeleteTarget(r)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <LocationForm open={formOpen} editing={editing}
        onClose={() => setFormOpen(false)} onSaved={() => load(search.trim() || undefined)} />

      <FhirViewer open={!!fhirTarget} resource={fhirTarget} onClose={() => setFhirTarget(null)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={LOC.deleteDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete location?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-medium">{deleteTarget?.name}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={LOC.confirmDelete} onClick={confirmDelete}
              className="emr-catalog__btn emr-catalog__btn--danger">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
